/**
 * IP グループ — 条件式の `ip:<名前>` が指す接続元の集まり。
 *
 * 1 件ずつ足すのではなく**貼り付けて一括で取り込む**(1 行 1 件。`,` 区切りでもよい)。
 * 受け付けるのは IPv4 / IPv6 のアドレスと CIDR だけで、**ホスト名は受け付けない**
 * (名前を引く判定にすると、ログインのたびに DNS に依存する)。
 */
import { DownloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { IpGroup } from "../../lib/api";
import { jst } from "../../lib/format";
import { errorText, useIdpPerms } from "./hooks";
import {
  useCreateIpGroup,
  useDeleteIpGroup,
  useIpGroups,
  useIpGroupsCsv,
  useUpdateIpGroup,
} from "./policyHooks";
import { ENTRIES_MAX, ENTRIES_PLACEHOLDER } from "./policyOptions";

const { Text, Paragraph } = Typography;

type Values = { displayName: string; note?: string; entries: string };

function EditModal({
  row,
  open,
  onClose,
}: {
  row: IpGroup | null;
  open: boolean;
  onClose: () => void;
}) {
  const [form] = Form.useForm<Values>();
  const create = useCreateIpGroup();
  const update = useUpdateIpGroup();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      displayName: row?.displayName ?? "",
      note: row?.note ?? "",
      entries: (row?.entries ?? []).join("\n"),
    });
  }, [open, row, form]);

  const submit = (v: Values) => {
    const data = {
      displayName: v.displayName,
      note: v.note ?? "",
      entries: v.entries ?? "",
    };
    const done = { onSuccess: onClose };
    if (row) update.mutate({ id: row.id, data }, done);
    else create.mutate(data, done);
  };

  return (
    <Modal
      open={open}
      title={row ? `${row.displayName} を編集` : "IP グループの追加"}
      onCancel={onClose}
      onOk={() => void form.submit()}
      okText={row ? "保存する" : "追加する"}
      cancelText="やめる"
      confirmLoading={create.isPending || update.isPending}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="displayName"
          label="名前(条件式に書く名前)"
          extra="条件式では ip:<名前> で参照する。名前に空白があるときは ip:&quot;社内 NW&quot; と引用符で囲む。"
          rules={[{ required: true, message: "名前は必須" }]}
        >
          <Input maxLength={100} placeholder="本社" />
        </Form.Item>
        <Form.Item name="note" label="メモ">
          <Input maxLength={200} placeholder="本社の固定回線" />
        </Form.Item>
        <Form.Item
          name="entries"
          label="IP アドレス / CIDR(1 行 1 件で貼り付ける)"
          extra={`${ENTRIES_MAX} 件まで。IPv4 / IPv6 とその CIDR。ホスト名は入れられない。`}
          rules={[{ required: true, message: "IP を 1 件以上入れる" }]}
        >
          <Input.TextArea rows={8} placeholder={ENTRIES_PLACEHOLDER} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function IpGroupsPage() {
  const { canAdmin } = useIdpPerms();
  const query = useIpGroups();
  const remove = useDeleteIpGroup();
  const csv = useIpGroupsCsv();
  const [editing, setEditing] = useState<IpGroup | null>(null);
  const [open, setOpen] = useState(false);

  /** 使われているグループは 409 になる。どこで使われているかを見せてから聞く。 */
  const askDelete = (row: IpGroup) => {
    Modal.confirm({
      title: `${row.displayName} を削除する`,
      content: row.inUse
        ? `この IP グループは ${row.usedBy.join(" / ")} で使われている。先に条件式を直す。`
        : `${row.count} 件のアドレスも一緒に消える。`,
      okText: "削除する",
      okButtonProps: { danger: true, disabled: row.inUse },
      cancelText: "やめる",
      onOk: () => remove.mutate(row.id),
    });
  };

  const columns: ColumnsType<IpGroup> = [
    {
      title: "名前",
      width: 160,
      dataIndex: "displayName",
      render: (v: string) => <Text strong>{v}</Text>,
    },
    { title: "件数", dataIndex: "count", width: 90 },
    {
      title: "中身",
      width: 320,
      onCell: (row) => ({ title: row.entries.join(", ") }),
      dataIndex: "entries",
      ellipsis: true,
      render: (entries: string[]) => (
        <Tooltip title={entries.join(", ")}>
          <Text code>{entries.slice(0, 3).join(", ")}</Text>
          {entries.length > 3 && <Text type="secondary"> ほか {entries.length - 3} 件</Text>}
        </Tooltip>
      ),
    },
    {
      title: "使われている場所",
      dataIndex: "usedBy",
      width: 320,
      render: (used: string[]) =>
        used.length ? (
          <Space size={[4, 4]} wrap>
            {used.map((u) => (
              <Tag key={u} color="blue">
                {u}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    { title: "メモ", width: 320, dataIndex: "note", ellipsis: true },
    {
      title: "更新",
      dataIndex: "updatedAt",
      width: 180,
      render: (v: string | null) => jst(v) || "—",
    },
    {
      title: "",
      key: "op",
      width: 180,
      render: (_: unknown, row) =>
        canAdmin && (
          <Space>
            <Button
              size="small"
              onClick={() => {
                setEditing(row);
                setOpen(true);
              }}
            >
              編集
            </Button>
            <Button size="small" danger onClick={() => askDelete(row)}>
              削除
            </Button>
          </Space>
        ),
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          IP グループ
        </Typography.Title>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={csv}>
            CSV
          </Button>
          {canAdmin && (
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
            >
              IP グループの追加
            </Button>
          )}
        </Space>
      </Flex>

      <Paragraph type="secondary">
        アクセスポリシーの条件式で <Text code>ip:&lt;名前&gt;</Text> と書くと、
        ここに入れた範囲からのログインだけが真になる。判定は接続元 IP をそのまま見るので、
        Keycloak の手前にプロキシが居る構成では先に転送元の設定を確認する。
      </Paragraph>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="IP グループを取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        <Table<IpGroup>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          loading={query.isFetching || remove.isPending}
          columns={columns}
          dataSource={query.data?.items ?? []}
          pagination={false}
          locale={{ emptyText: "IP グループはまだ無い" }}
        />
      </Card>

      <EditModal row={editing} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
